import LoadingRows from './LoadingRows.jsx';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import TeamLogo from './TeamLogo';
import { getRankings } from '../services/rankings';
import { ACTIVE_WOMENS_SEASON, WOMENS_SEASONS } from '../data/seasonConfig';
import '../styles/rankings.css';

const FAVORITES_STORAGE_KEY = 'volleywire.favoriteTeams.v1';
const FAVORITES_EVENT = 'volleywire:favorites-changed';
const DEFAULT_SEASON = ACTIVE_WOMENS_SEASON;
const FALLBACK_SEASONS = WOMENS_SEASONS;

function readInitialSeason() {
  if (typeof window === 'undefined') {
    return DEFAULT_SEASON;
  }

  const value = Number(
    new URLSearchParams(window.location.search).get('rankingSeason')
  );

  return FALLBACK_SEASONS.includes(value) ? value : DEFAULT_SEASON;
}

function writeRankingSeason(season, mode = 'replace') {
  const url = new URL(window.location.href);
  url.searchParams.delete('sport');
  url.searchParams.set('rankingSeason', String(season));
  url.searchParams.delete('team');
  url.searchParams.delete('teamSeason');
  url.searchParams.delete('match');
  url.hash = 'rankings';
  window.history[mode === 'push' ? 'pushState' : 'replaceState'](
    {},
    '',
    url.toString()
  );
}

function normalizeTeamKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '');
}

function getFavoriteKey(value) {
  const key = normalizeTeamKey(value);
  return key ? `name:${key}` : '';
}

function getTeamFavoriteKeys(team) {
  return Array.from(
    new Set(
      [team?.name, team?.longName, ...(team?.aliases || [])]
        .map(getFavoriteKey)
        .filter(Boolean)
    )
  );
}

function readFavorites() {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const stored = window.localStorage.getItem(FAVORITES_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];

    return Array.isArray(parsed)
      ? Array.from(
          new Set(
            parsed
              .filter((value) => typeof value === 'string')
              .map((value) => value.trim())
              .filter(Boolean)
          )
        )
      : [];
  } catch {
    return [];
  }
}

function saveFavorites(favorites) {
  try {
    window.localStorage.setItem(
      FAVORITES_STORAGE_KEY,
      JSON.stringify(favorites)
    );
    window.dispatchEvent(
      new CustomEvent(FAVORITES_EVENT, { detail: { favorites } })
    );
  } catch {
    // Favorites still remain usable for the current page session.
  }
}

function isFavorite(team, favoriteSet) {
  return getTeamFavoriteKeys(team).some((key) => favoriteSet.has(key));
}

function formatDate(value) {
  const text = String(value || '').trim();

  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${text}T12:00:00Z`));
}

function formatFinish(item, compact = false) {
  const finish = item?.seasonFinish;

  if (!finish) {
    return '';
  }

  if (compact) {
    return finish.label || finish.display || '';
  }

  return finish.display || finish.label || '';
}

function Movement({ movement }) {
  if (!movement || movement.type === 'unavailable') {
    return <span className="rankings-movement unavailable">—</span>;
  }

  const symbol =
    movement.type === 'up'
      ? '↑'
      : movement.type === 'down'
        ? '↓'
        : movement.type === 'new'
          ? 'NR'
          : '=';
  const amount = movement.amount ? ` ${movement.amount}` : '';

  return (
    <span
      className={`rankings-movement ${movement.type}`}
      title={movement.label || undefined}
      aria-label={movement.label || 'No movement data'}
    >
      {symbol}
      {amount}
    </span>
  );
}

function FavoriteButton({ item, active, onToggle, compact = false }) {
  return (
    <button
      type="button"
      className={`rankings-favorite${active ? ' active' : ''}${
        compact ? ' compact' : ''
      }`}
      aria-pressed={active}
      aria-label={
        active
          ? `Remove ${item.team.name} from My Teams`
          : `Add ${item.team.name} to My Teams`
      }
      onClick={(event) => {
        event.stopPropagation();
        onToggle(item);
      }}
    >
      <span aria-hidden="true">★</span>
    </button>
  );
}

function FeaturedRanking({ item, poll, favorite, onOpenTeam, onToggleFavorite }) {
  if (!item) {
    return null;
  }

  return (
    <article className="rankings-featured">
      <button
        type="button"
        className="rankings-featured-open"
        onClick={() => onOpenTeam(item.team, item.season)}
        aria-label={`Open ${item.team.name} team profile`}
      >
        <div className="rankings-featured-rank">
          <span>{item.season === ACTIVE_WOMENS_SEASON ? 'Current' : 'Final'}</span>
          <strong>{item.rankLabel}</strong>
          <span className="rankings-featured-mobile-movement"><Movement movement={item.movement} /></span>
        </div>

        <div className="rankings-featured-logo">
          <TeamLogo team={item.team} variant="match-center" />
        </div>

        <div className="rankings-featured-copy">
          <p className="eyebrow coral">
            {poll?.archiveType === 'official-preseason-poll' ? 'Preseason No. 1' : 'No. 1 team'}
          </p>
          <h2>{item.team.name}</h2>
          {(item.record || formatFinish(item)) ? (
            <div className="rankings-featured-facts">
              {item.record ? <span>{item.record}</span> : null}
              {formatFinish(item) ? <span>{formatFinish(item)}</span> : null}
            </div>
          ) : null}
        </div>

        <div className="rankings-featured-poll">
          <Movement movement={item.movement} />
          {Number.isInteger(item.points) ? (
            <span>{item.points.toLocaleString()} pts</span>
          ) : null}
          {Number.isInteger(item.firstPlaceVotes) ? (
            <span>{item.firstPlaceVotes} first-place votes</span>
          ) : null}
        </div>

        <span className="rankings-open-arrow" aria-hidden="true">
          ›
        </span>
      </button>

      <FavoriteButton
        item={item}
        active={favorite}
        onToggle={onToggleFavorite}
      />
    </article>
  );
}

function RankingRow({ item, favorite, onOpenTeam, onToggleFavorite }) {
  return (
    <article className="rankings-row">
      <button
        type="button"
        className="rankings-row-open"
        onClick={() => onOpenTeam(item.team, item.season)}
        aria-label={`Open ${item.team.name} team profile`}
      >
        <div className="rankings-row-rank">
          <strong>{item.rankLabel}</strong>
          <Movement movement={item.movement} />
        </div>

        <div className="rankings-row-logo">
          <TeamLogo team={item.team} variant="scoreboard" />
        </div>

        <div className="rankings-row-team">
          <strong>{item.team.name}</strong>
          {(item.record || formatFinish(item, true)) ? (
            <span>
              {item.record || null}
              {item.record && formatFinish(item, true) ? <i aria-hidden="true">·</i> : null}
              {formatFinish(item, true) || null}
            </span>
          ) : null}
        </div>

        <div className="rankings-row-poll">
          {Number.isInteger(item.points) ? (
            <span>
              <strong>{item.points.toLocaleString()}</strong>
              <small>PTS</small>
            </span>
          ) : null}
          {Number.isInteger(item.firstPlaceVotes) ? (
            <span>
              <strong>{item.firstPlaceVotes}</strong>
              <small>FPV</small>
            </span>
          ) : null}
        </div>

        <span className="rankings-open-arrow" aria-hidden="true">
          ›
        </span>
      </button>

      <FavoriteButton
        item={item}
        active={favorite}
        onToggle={onToggleFavorite}
        compact
      />
    </article>
  );
}

function RankingsState({ symbol, title, message, onRetry = null }) {
  return (
    <div className="rankings-state" role="status">
      <span aria-hidden="true">{symbol}</span>
      <h2>{title}</h2>
      {message ? <p>{message}</p> : null}
      {onRetry ? (
        <button type="button" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

export default function RankingsPage({ onOpenTeam }) {
  const [season, setSeason] = useState(readInitialSeason);
  const [favorites, setFavorites] = useState(readFavorites);
  const [state, setState] = useState({
    loading: true,
    error: '',
    rankings: [],
    poll: null,
    availableSeasons: FALLBACK_SEASONS,
  });
  const controllerRef = useRef(null);
  const requestIdRef = useRef(0);

  const loadRankings = useCallback(({ silent = false } = {}) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    const requestId = requestIdRef.current + 1;
    controllerRef.current = controller;
    requestIdRef.current = requestId;

    if (!silent) setState((current) => ({ ...current, loading: true, error: '' }));

    getRankings({ season, signal: controller.signal, force: silent })
      .then((payload) => {
        if (requestId !== requestIdRef.current) {
          return;
        }

        setState((current) => {
          const currentDate = current.poll?.publishedDate || current.poll?.throughDate || '';
          const incomingDate = payload.poll?.publishedDate || payload.poll?.throughDate || '';
          if (payload.stale && current.rankings.length && currentDate > incomingDate) {
            return { ...current, loading: false, stale: true };
          }
          return {
            loading: false,
            error: '',
            stale: payload.stale,
            fallback: payload.fallback,
            rankings: payload.rankings,
            poll: payload.poll,
            availableSeasons: payload.availableSeasons.length
              ? payload.availableSeasons
              : FALLBACK_SEASONS,
          };
        });
      })
      .catch((error) => {
        if (
          error?.name === 'AbortError' ||
          requestId !== requestIdRef.current
        ) {
          return;
        }

        setState((current) => {
          if (silent && current.rankings.length) return { ...current, loading: false, stale: true };
          return {
            ...current,
            loading: false,
            rankings: [],
            error: error instanceof Error ? error.message : 'Rankings are temporarily unavailable.',
          };
        });
      });
  }, [season]);

  useEffect(() => {
    writeRankingSeason(season);
    loadRankings();

    return () => controllerRef.current?.abort();
  }, [loadRankings, season]);

  useEffect(() => {
    if (season !== ACTIVE_WOMENS_SEASON) return;
    const refresh = () => {
      if (document.visibilityState === 'visible') loadRankings({ silent: true });
    };
    const timer = window.setInterval(refresh, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [loadRankings, season]);

  useEffect(() => {
    function handlePopState() {
      const nextSeason = readInitialSeason();
      setSeason((current) => (current === nextSeason ? current : nextSeason));
    }

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    function handleFavorites(event) {
      const next = Array.isArray(event?.detail?.favorites)
        ? event.detail.favorites
        : readFavorites();
      setFavorites(next);
    }

    function handleStorage(event) {
      if (!event || event.key === FAVORITES_STORAGE_KEY) {
        setFavorites(readFavorites());
      }
    }

    window.addEventListener(FAVORITES_EVENT, handleFavorites);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(FAVORITES_EVENT, handleFavorites);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const favoriteSet = useMemo(() => new Set(favorites), [favorites]);
  const filtered = state.rankings;
  const featured = filtered.find((item) => item.rank === 1) || null;
  const rows = featured
    ? filtered.filter((item) => item.id !== featured.id)
    : filtered;

  function changeSeason(event) {
    const next = Number(event.target.value);

    if (!state.availableSeasons.includes(next) || next === season) {
      return;
    }

    setSeason(next);
    writeRankingSeason(next, 'push');
  }

  function toggleFavorite(item) {
    const keys = getTeamFavoriteKeys(item.team);
    const primaryKey = getFavoriteKey(item.team.name);
    const current = new Set(favorites);
    const active = keys.some((key) => current.has(key));

    if (active) {
      keys.forEach((key) => current.delete(key));
    } else if (primaryKey) {
      current.add(primaryKey);
    }

    const next = Array.from(current);
    setFavorites(next);
    saveFavorites(next);
  }

  function openTeam(team, selectedSeason) {
    if (typeof onOpenTeam === 'function') {
      onOpenTeam(team, selectedSeason);
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('sport');
    url.searchParams.set('team', team.slug);
    url.searchParams.set('teamSeason', String(selectedSeason));
    url.searchParams.delete('match');
    url.hash = 'teams';
    window.history.pushState({}, '', url.toString());
    window.dispatchEvent(new PopStateEvent('popstate'));
  }

  const publishedDate = formatDate(state.poll?.publishedDate);

  return (
    <main className="rankings-page" id="rankings">
      <section className="rankings-section">
        <div className="shell">
          <div className="rankings-heading">
            <div>
              <p className="eyebrow coral">Women’s Division I</p>
              <h1>AVCA Rankings</h1>
              <p>
                {season === ACTIVE_WOMENS_SEASON
                  ? 'The latest published AVCA poll with current team records.'
                  : 'Final national positions, records, and NCAA tournament results.'}
              </p>
            </div>

            <label className="rankings-season-picker">
              <span>Season</span>
              <select
                value={season}
                disabled={state.loading}
                onChange={changeSeason}
              >
                {state.availableSeasons.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="rankings-summary">
            <div>
              <strong>
                {season === ACTIVE_WOMENS_SEASON
                  ? `Latest available: ${state.poll?.label || `${season} AVCA Poll`}`
                  : `${season} Final Poll`}
              </strong>
              <span>{state.poll?.name || 'AVCA Division I Coaches Poll'}</span>
            </div>
            <div>
              <strong>{state.rankings.length || '—'} ranked teams</strong>
              <span>{publishedDate ? `Published ${publishedDate}` : state.poll?.throughDate ? `Games through ${formatDate(state.poll.throughDate)} · release date not supplied` : state.loading ? 'Checking latest poll…' : 'Publication date unavailable'}</span>
            </div>
          </div>

          {state.stale ? <p className="rankings-source-notice" role="status">
            {state.fallback
              ? 'The latest poll could not be reached. Showing the dated preseason archive—not current rankings. We’ll retry automatically.'
              : 'The latest poll could not be refreshed. Showing the last available poll; we’ll retry automatically.'}
          </p> : null}

          {state.loading ? (
            <LoadingRows label={`Loading ${season} rankings…`} />
          ) : state.error ? (
            <RankingsState
              symbol="!"
              title="We couldn’t load the rankings."
              message={state.error}
              onRetry={loadRankings}
            />
          ) : filtered.length ? (
            <>
              <FeaturedRanking
                item={featured}
                poll={state.poll}
                favorite={featured ? isFavorite(featured.team, favoriteSet) : false}
                onOpenTeam={openTeam}
                onToggleFavorite={toggleFavorite}
              />

              <div className="rankings-list" aria-label={`${season} AVCA rankings`}>
                <div className="rankings-list-header" aria-hidden="true">
                  <span>Rank</span>
                  <span>Team</span>
                  <span>Poll</span>
                </div>
                {rows.map((item) => (
                  <RankingRow
                    key={item.id}
                    item={item}
                    favorite={isFavorite(item.team, favoriteSet)}
                    onOpenTeam={openTeam}
                    onToggleFavorite={toggleFavorite}
                  />
                ))}
              </div>
            </>
          ) : (
            <RankingsState
              symbol="⌕"
              title="No ranked teams match."
              message="The selected poll did not contain ranked teams."
            />
          )}

          {!state.loading && !state.error && state.poll?.sourceNote ? (
            <p className="rankings-source-note">{state.poll.sourceNote}</p>
          ) : null}
        </div>
      </section>
    </main>
  );
}
